import { useRef, useState, useEffect } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import {
    Brain,
    KanbanSquare,
    Building2,
    FileText,
    MessageSquare,
    Sparkles,
    Receipt,
    UserCog,
    Workflow,
    Activity,
    Zap,
    Globe2,
    CheckCircle2,
} from "lucide-react";

/**
 * Live service-mesh topology — central ApexBrain core orbited by 8 module
 * nodes, connected via animated SVG beams with traveling data packets.
 * Sits behind an abstract topographic + iso-grid backdrop.
 */
const NODES = [
    { id: "projects", label: "Projects", icon: KanbanSquare, angle: -90, color: "#06b6d4", text: "text-cyan-300" },
    { id: "crm", label: "CRM", icon: Building2, angle: -45, color: "#10b981", text: "text-emerald-300" },
    { id: "docs", label: "Docs", icon: FileText, angle: 0, color: "#fbbf24", text: "text-amber-glow" },
    { id: "chat", label: "Chat", icon: MessageSquare, angle: 45, color: "#a78bfa", text: "text-violet-300" },
    { id: "ai", label: "AI", icon: Sparkles, angle: 90, color: "#fbbf24", text: "text-amber-glow" },
    { id: "finance", label: "Finance", icon: Receipt, angle: 135, color: "#34d399", text: "text-emerald-300" },
    { id: "hr", label: "HR", icon: UserCog, angle: 180, color: "#818cf8", text: "text-indigo-300" },
    { id: "auto", label: "Automations", icon: Workflow, angle: 225, color: "#22d3ee", text: "text-cyan-300" },
];

const VBOX = 800; // square viewBox
const CENTER = VBOX / 2;
const RADIUS_X = 320; // ellipse horizontal radius (for slight squish on wider layouts)
const RADIUS_Y = 250;

const polarToXY = (angleDeg, rx = RADIUS_X, ry = RADIUS_Y) => {
    const rad = (angleDeg * Math.PI) / 180;
    return {
        x: CENTER + Math.cos(rad) * rx,
        y: CENTER + Math.sin(rad) * ry,
    };
};

function useMaxWidth(maxPx) {
    const [matches, setMatches] = useState(
        () => typeof window !== "undefined" && window.matchMedia(`(max-width: ${maxPx}px)`).matches,
    );
    useEffect(() => {
        const mq = window.matchMedia(`(max-width: ${maxPx}px)`);
        const onChange = () => setMatches(mq.matches);
        onChange();
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, [maxPx]);
    return matches;
}

export const InfrastructureMesh = () => {
    const isMobile = useMaxWidth(639);

    if (isMobile) {
        return <MobileInfrastructureMesh />;
    }

    return <DesktopInfrastructureMesh />;
};

const DesktopInfrastructureMesh = () => {
    const ref = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 60, damping: 18 });
    const sy = useSpring(my, { stiffness: 60, damping: 18 });
    const rotateY = useTransform(sx, [-0.5, 0.5], [-10, 10]);
    const rotateX = useTransform(sy, [-0.5, 0.5], [6, -6]);

    const onMove = (e) => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        mx.set((e.clientX - rect.left) / rect.width - 0.5);
        my.set((e.clientY - rect.top) / rect.height - 0.5);
    };
    const onLeave = () => {
        mx.set(0);
        my.set(0);
    };

    return (
        <div
            ref={ref}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            className="relative w-full mx-auto max-w-5xl overflow-hidden"
            style={{ perspective: "1400px" }}
            data-testid="infra-mesh"
        >
            <motion.div
                style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
                className="relative w-full aspect-square sm:aspect-[4/3] lg:aspect-[16/10]"
            >
                {/* Abstract topographic + Voronoi backdrop */}
                <TopographicBackdrop />

                {/* Iso grid floor */}
                <IsoGridFloor />

                {/* SVG beams + nodes */}
                <svg
                    viewBox={`0 0 ${VBOX} ${VBOX}`}
                    className="absolute inset-0 w-full h-full"
                    preserveAspectRatio="xMidYMid meet"
                >
                    <defs>
                        {NODES.map((n) => (
                            <linearGradient
                                key={`grad-${n.id}`}
                                id={`grad-${n.id}`}
                                gradientUnits="userSpaceOnUse"
                                x1={CENTER}
                                y1={CENTER}
                                x2={polarToXY(n.angle).x}
                                y2={polarToXY(n.angle).y}
                            >
                                <stop offset="0%" stopColor={n.color} stopOpacity="0" />
                                <stop offset="50%" stopColor={n.color} stopOpacity="0.6" />
                                <stop offset="100%" stopColor={n.color} stopOpacity="0.15" />
                            </linearGradient>
                        ))}
                        <radialGradient id="coreGlow" cx="50%" cy="50%" r="50%">
                            <stop offset="0%" stopColor="#5eead4" stopOpacity="0.5" />
                            <stop offset="60%" stopColor="#06b6d4" stopOpacity="0.18" />
                            <stop offset="100%" stopColor="#06b6d4" stopOpacity="0" />
                        </radialGradient>
                    </defs>

                    {/* Core glow halo */}
                    <circle cx={CENTER} cy={CENTER} r="200" fill="url(#coreGlow)" />

                    {/* Concentric ripple rings around core */}
                    {[1, 2, 3].map((i) => (
                        <motion.circle
                            key={i}
                            cx={CENTER}
                            cy={CENTER}
                            r="56"
                            fill="none"
                            stroke="#5eead4"
                            strokeWidth="1"
                            initial={{ opacity: 0.6, scale: 1 }}
                            animate={{
                                opacity: [0.5, 0],
                                scale: [1, 3.5],
                            }}
                            transition={{
                                duration: 4,
                                delay: i * 1.3,
                                repeat: Infinity,
                                ease: "easeOut",
                            }}
                            style={{ transformOrigin: `${CENTER}px ${CENTER}px` }}
                        />
                    ))}

                    {/* Beams center → satellites */}
                    {NODES.map((n) => {
                        const p = polarToXY(n.angle);
                        return (
                            <g key={`beam-${n.id}`}>
                                <line
                                    x1={CENTER}
                                    y1={CENTER}
                                    x2={p.x}
                                    y2={p.y}
                                    stroke={`url(#grad-${n.id})`}
                                    strokeWidth="1.4"
                                    strokeLinecap="round"
                                />
                                {/* Dashed overlay for tech feel */}
                                <line
                                    x1={CENTER}
                                    y1={CENTER}
                                    x2={p.x}
                                    y2={p.y}
                                    stroke={n.color}
                                    strokeWidth="0.6"
                                    strokeOpacity="0.4"
                                    strokeDasharray="3 6"
                                />
                            </g>
                        );
                    })}

                    {/* Data packets traveling outward */}
                    {NODES.map((n, i) => {
                        const p = polarToXY(n.angle);
                        return (
                            <motion.circle
                                key={`packet-${n.id}`}
                                r="3.5"
                                fill={n.color}
                                filter="url(#glow)"
                                initial={{ cx: CENTER, cy: CENTER, opacity: 0 }}
                                animate={{
                                    cx: [CENTER, p.x],
                                    cy: [CENTER, p.y],
                                    opacity: [0, 1, 1, 0],
                                }}
                                transition={{
                                    duration: 1.8,
                                    delay: i * 0.35,
                                    repeat: Infinity,
                                    repeatDelay: 2.2,
                                    ease: "easeInOut",
                                }}
                            />
                        );
                    })}

                    {/* Subtle glow filter */}
                    <filter id="glow">
                        <feGaussianBlur stdDeviation="2" result="b" />
                        <feMerge>
                            <feMergeNode in="b" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </svg>

                {/* HTML node layer (positioned over SVG via percentages) */}
                <div className="absolute inset-0">
                    {/* Central core */}
                    <div
                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                        style={{ transform: "translate(-50%, -50%) translateZ(40px)" }}
                    >
                        <CoreNode />
                    </div>

                    {/* Orbital nodes */}
                    {NODES.map((n, i) => {
                        const x = (polarToXY(n.angle).x / VBOX) * 100;
                        const y = (polarToXY(n.angle).y / VBOX) * 100;
                        return (
                            <motion.div
                                key={n.id}
                                initial={{ opacity: 0, scale: 0.7 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{
                                    duration: 0.6,
                                    delay: 0.3 + i * 0.07,
                                    ease: "easeOut",
                                }}
                                style={{
                                    left: `${x}%`,
                                    top: `${y}%`,
                                    transform: "translate(-50%, -50%) translateZ(20px)",
                                }}
                                className="absolute"
                                data-testid={`mesh-node-${n.id}`}
                            >
                                <Node node={n} />
                            </motion.div>
                        );
                    })}
                </div>

                {/* Floating stat badges */}
                <FloatingStats />
            </motion.div>
        </div>
    );
};

/* ─────────────── Mobile layout (grid, no overlapping orbit) ─────────────── */
const MOBILE_STATS = [
    { icon: CheckCircle2, label: "Uptime", value: "99.997%", color: "text-emerald-300" },
    { icon: Zap, label: "Throughput", value: "1.4M/s", color: "text-amber-glow" },
    { icon: Globe2, label: "Regions", value: "14 PoPs", color: "text-violet-300" },
    { icon: Activity, label: "p95", value: "12 ms", color: "text-cyan-300" },
];

const MobileInfrastructureMesh = () => (
    <div
        className="relative w-full max-w-md mx-auto"
        data-testid="infra-mesh-mobile"
    >
        <div className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-b from-obsidian-100/80 to-obsidian-200/40 backdrop-blur-xl px-4 py-6 sm:px-5">
            {/* Ambient glow */}
            <div
                aria-hidden
                className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_80%_50%_at_50%_0%,rgba(94,234,212,0.12),transparent_70%)]"
            />

            {/* Core */}
            <div className="relative flex flex-col items-center">
                <CoreNode compact />
                <p className="mt-3 font-code text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    8 modules · 1 core
                </p>
            </div>

            {/* Module grid */}
            <div className="relative mt-6 grid grid-cols-4 gap-2">
                {NODES.map((n, i) => (
                    <motion.div
                        key={n.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15 + i * 0.04, duration: 0.4 }}
                    >
                        <MobileModuleChip node={n} />
                    </motion.div>
                ))}
            </div>

            {/* Stats strip */}
            <div className="relative mt-5 grid grid-cols-2 gap-2">
                {MOBILE_STATS.map((s, i) => (
                    <motion.div
                        key={s.label}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 + i * 0.06, duration: 0.4 }}
                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06]"
                    >
                        <s.icon className={`w-3.5 h-3.5 shrink-0 ${s.color}`} />
                        <div className="min-w-0 leading-tight">
                            <div className="font-code text-[8px] uppercase tracking-wider text-zinc-500 truncate">
                                {s.label}
                            </div>
                            <div className="font-display text-xs font-semibold text-white tabular-nums">
                                {s.value}
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Live pulse bar */}
            <div className="relative mt-4 flex items-center justify-center gap-2 text-[10px] font-code text-zinc-500">
                <span className="relative flex w-1.5 h-1.5">
                    <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
                    <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-400" />
                </span>
                All systems operational
            </div>
        </div>
    </div>
);

const MobileModuleChip = ({ node }) => (
    <div
        className="flex flex-col items-center gap-1.5 p-2 rounded-xl bg-obsidian-200/50 border border-white/[0.06] active:scale-95 transition-transform"
        data-testid={`mesh-node-${node.id}`}
    >
        <span
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{
                background: `${node.color}1a`,
                border: `1px solid ${node.color}35`,
                boxShadow: `0 0 16px -6px ${node.color}55`,
            }}
        >
            <node.icon className={`w-4 h-4 ${node.text}`} strokeWidth={1.7} />
        </span>
        <span className="font-display text-[9px] font-medium text-white text-center leading-tight line-clamp-2">
            {node.label}
        </span>
    </div>
);

/* ─────────────── Central core ─────────────── */
const CoreNode = ({ compact = false }) => (
    <motion.div
        animate={{
            boxShadow: compact
                ? [
                      "0 0 0 0 rgba(94,234,212,0.4), 0 0 40px 0 rgba(94,234,212,0.25)",
                      "0 0 0 8px rgba(94,234,212,0), 0 0 50px 6px rgba(94,234,212,0.35)",
                      "0 0 0 0 rgba(94,234,212,0.4), 0 0 40px 0 rgba(94,234,212,0.25)",
                  ]
                : [
                      "0 0 0 0 rgba(94,234,212,0.5), 0 0 60px 0 rgba(94,234,212,0.35)",
                      "0 0 0 12px rgba(94,234,212,0), 0 0 80px 10px rgba(94,234,212,0.45)",
                      "0 0 0 0 rgba(94,234,212,0.5), 0 0 60px 0 rgba(94,234,212,0.35)",
                  ],
        }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        className={
            compact
                ? "relative w-16 h-16 rounded-xl flex flex-col items-center justify-center bg-gradient-to-br from-cyan-500/30 via-cyan-700/20 to-emerald-500/20 border border-cyan-400/40 backdrop-blur-xl"
                : "relative w-24 h-24 sm:w-28 sm:h-28 rounded-2xl flex flex-col items-center justify-center bg-gradient-to-br from-cyan-500/30 via-cyan-700/20 to-emerald-500/20 border border-cyan-400/40 backdrop-blur-xl"
        }
    >
        <Brain
            className={compact ? "w-6 h-6 text-cyan-300" : "w-7 h-7 text-cyan-300"}
            strokeWidth={1.5}
        />
        <span
            className={
                compact
                    ? "font-display text-[8px] uppercase tracking-[0.18em] text-cyan-200 mt-1"
                    : "font-display text-[10px] uppercase tracking-[0.2em] text-cyan-200 mt-1.5"
            }
        >
            Core
        </span>
        {!compact && (
            <span className="font-code text-[9px] text-cyan-400/70 mt-0.5">v2.4.0</span>
        )}
    </motion.div>
);

/* ─────────────── Satellite node ─────────────── */
const Node = ({ node }) => (
    <div className="group relative">
        <div
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-obsidian-100/90 border border-white/[0.08] backdrop-blur-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.6)] hover:border-white/20 transition-colors"
            style={{ boxShadow: `0 0 0 1px rgba(255,255,255,0.04), 0 0 28px -8px ${node.color}66` }}
        >
            <span
                className="w-7 h-7 rounded-md flex items-center justify-center"
                style={{ background: `${node.color}1f`, border: `1px solid ${node.color}40` }}
            >
                <node.icon className={`w-3.5 h-3.5 ${node.text}`} strokeWidth={1.7} />
            </span>
            <div className="leading-tight">
                <div className="font-display text-xs font-semibold text-white">
                    {node.label}
                </div>
                <div className="font-code text-[9px] text-zinc-500 flex items-center gap-1">
                    <span
                        className="w-1 h-1 rounded-full"
                        style={{ background: node.color }}
                    />
                    healthy
                </div>
            </div>
        </div>
    </div>
);

/* ─────────────── Abstract topographic backdrop (SVG contours) ─────────────── */
const TopographicBackdrop = () => (
    <svg
        viewBox="0 0 800 600"
        className="absolute inset-0 w-full h-full opacity-[0.18] pointer-events-none"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden
    >
        <defs>
            <radialGradient id="topo-fade" cx="50%" cy="50%" r="60%">
                <stop offset="0%" stopColor="#fff" stopOpacity="1" />
                <stop offset="100%" stopColor="#fff" stopOpacity="0" />
            </radialGradient>
            <mask id="topo-mask">
                <rect width="800" height="600" fill="url(#topo-fade)" />
            </mask>
        </defs>
        <g mask="url(#topo-mask)" stroke="#5eead4" strokeWidth="0.4" fill="none">
            {[40, 80, 120, 160, 200, 240, 280, 320].map((r, i) => (
                <path
                    key={i}
                    d={`M ${400 - r} 300 q 0 ${-r * 0.8} ${r} ${-r * 0.4} q ${r} ${r * 0.4} ${r * 0.4} ${r * 0.9} q ${-r * 0.4} ${r * 0.5} ${-r * 1.2} ${r * 0.1} q ${-r * 0.4} ${-r * 0.6} ${-r * 0.2} ${-r * 0.6}`}
                    strokeOpacity={0.15 + i * 0.05}
                />
            ))}
        </g>
    </svg>
);

/* ─────────────── Isometric grid floor ─────────────── */
const IsoGridFloor = () => (
    <div
        aria-hidden
        className="absolute left-1/2 bottom-0 w-[140%] h-[70%] -translate-x-1/2 opacity-[0.18] pointer-events-none"
        style={{
            transform: "translateX(-50%) rotateX(72deg) translateZ(-40px)",
            transformOrigin: "center bottom",
            backgroundImage:
                "linear-gradient(rgba(94,234,212,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(94,234,212,0.3) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage:
                "radial-gradient(ellipse at center, black 30%, transparent 75%)",
            WebkitMaskImage:
                "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        }}
    />
);

/* ─────────────── Floating stats ─────────────── */
const FloatingStats = () => (
    <>
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            style={{ transform: "translateZ(80px)" }}
            className="hidden md:flex absolute top-3 left-0 lg:left-4 items-center gap-3 px-3 py-2 rounded-lg glass-strong"
        >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow" />
            <div className="leading-tight">
                <div className="font-code text-[9px] uppercase tracking-wider text-zinc-500">
                    Uptime
                </div>
                <div className="font-display text-sm font-semibold text-white tabular-nums">
                    99.997%
                </div>
            </div>
        </motion.div>

        <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            style={{ transform: "translateZ(80px)" }}
            className="hidden md:flex absolute top-3 right-0 lg:right-4 items-center gap-3 px-3 py-2 rounded-lg glass-strong"
        >
            <Zap className="w-3.5 h-3.5 text-amber-glow" />
            <div className="leading-tight">
                <div className="font-code text-[9px] uppercase tracking-wider text-zinc-500">
                    Throughput
                </div>
                <div className="font-display text-sm font-semibold text-white tabular-nums">
                    1.4M req/s
                </div>
            </div>
        </motion.div>

        <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.3, duration: 0.7 }}
            style={{ transform: "translateZ(80px)" }}
            className="hidden md:flex absolute bottom-3 right-0 lg:right-4 items-center gap-3 px-3 py-2 rounded-lg glass-strong"
        >
            <Activity className="w-3.5 h-3.5 text-cyan-300" />
            <div className="leading-tight">
                <div className="font-code text-[9px] uppercase tracking-wider text-zinc-500">
                    p95 latency
                </div>
                <div className="font-display text-sm font-semibold text-white tabular-nums">
                    12 ms
                </div>
            </div>
        </motion.div>

        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.5, duration: 0.7 }}
            style={{ transform: "translateZ(80px)" }}
            className="hidden md:flex absolute bottom-3 left-0 lg:left-4 items-center gap-3 px-3 py-2 rounded-lg glass-strong"
        >
            <Globe2 className="w-3.5 h-3.5 text-violet-300" />
            <div className="leading-tight">
                <div className="font-code text-[9px] uppercase tracking-wider text-zinc-500">
                    Regions
                </div>
                <div className="font-display text-sm font-semibold text-white tabular-nums">
                    14 PoPs
                </div>
            </div>
        </motion.div>
    </>
);
